<template>
  <div>
    <!-- mobile view -->
    <div class="pb-6 md:hidden">
      <template v-if="!selectedFolderId">
        <div class="flex items-center justify-between mx-4" v-if="folders.length !== 0">
          <h3 class="font-bold">
            {{ $i18nPresenter.t('Folders') }}
          </h3>

          <sort-by simplified />
        </div>

        <div class="flex overflow-x-auto flex-nowrap">
          <div
            class="flex-shrink-0 w-40 p-3 mt-4 mb-4 ml-4 rounded  bg-base-0"
            :key="`folder-${folder.folderId}`"
            :class="{'dark-folder': isDarkTheme, 'shadow-card':!isDarkTheme}"
            v-for="folder in folders"
          >
            <div
              class="flex justify-between"
            >
              <aha-icon-container
                class="my-3 font-bold truncate cursor-pointer"
                size="lg"
                icon-class="bg-purple-10 text-purple-50"
                @click="toFolderDetails(folder)"
              >
                <aha-icon name="folder" />
              </aha-icon-container>
            </div>
            <div
              :title="folder.folderName"
              class="my-3 font-bold truncate cursor-pointer hover:text-primary-90"
              @click="toFolderDetails(folder)"
            >
              {{ folder.folderName }}
            </div>
            <div class="flex items-center">
              <aha-icon-container>
                <aha-icon
                  name="file"
                  viewBox="0 0 24 24"
                  width="12"
                  height="12"
                />
              </aha-icon-container>
              <span class="text-xs text-base-80 text-dark-theme">
                <span
                  v-if="folder.presentations.length <= 1"
                  v-html="$i18nPresenter.t('xPresentation', {
                    length: folder.presentations.length
                  })"
                >
                </span>
                <span
                  v-else
                  v-html="$i18nPresenter.t('yPresentations', {
                    length: folder.presentations.length
                  })"
                >
                </span>
              </span>
            </div>
          </div>
        </div>
      </template>

      <div class="flex items-center justify-between mx-4" v-if="presentations.length !== 0">
        <h3 class="font-bold">
          {{ $i18nPresenter.t('Presentations') }}
        </h3>
        <sort-by simplified v-if="folders.length === 0 || selectedFolderId" />
      </div>

      <div
        :key="`presentation-${presentation.id}`"
        v-for="presentation in presentations"
        class="flex p-3 mx-4 my-4 rounded  bg-base-0"
        :class="{'dark-folder': isDarkTheme, 'shadow-card': !isDarkTheme}"
      >
        <span
          @click="goToPresentationDetail(presentation)"
        >
          <presentation-thumbnail :presentation="presentation" />
        </span>
        <span class="flex flex-col justify-between flex-grow w-0 ml-4">
          <div
            class="font-bold truncate cursor-pointer"
            :title="presentation.name"
            @click="goToPresentationDetail(presentation)"
          >
            <span
              class="text-base-60 text-link"
              v-if="!isSharedPresentation(presentation)"
            >
              {{ getFolderName(presentation) }}</span>
            <span class="text-link text-light-in-dark-theme">{{ presentation.name }}</span>
          </div>
          <div class="flex justify-between">
            <span class="flex flex-wrap items-center leading-6">
              <PresentationMode :presentation="presentation" class="min-w-max" />

              <span class="hidden ml-4 min-w-max 2xs:inline">
                <aha-icon-container>
                  <aha-icon
                    name="slides"
                    width="10"
                    height="10"
                    viewBox="0 0 12 12"
                  />
                </aha-icon-container>
                <span class="text-xs text-base-80">
                  {{ presentation.slideCount }}
                </span>
              </span>

              <span class="hidden ml-4 min-w-max 2xs:inline">
                <aha-icon-container>
                  <aha-icon
                    name="people"
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                  />
                </aha-icon-container>
                <span class="text-xs text-base-80">
                  <aha-formatter
                    :value="presentation.participantsCount || presentation.onlineCount"
                    :locale="presentation.language"
                    type="number"
                  />
                </span>
              </span>

            </span>
          </div>
        </span>
      </div>
    </div>

    <!-- desktop view -->
    <table class="hidden main-table md:table md:px-10">
      <thead>
        <tr>
          <table-heading name="thumbnail">
            <span
              class="inline-flex items-center cursor-pointer"
              @click="handleSortPresentations('name')"
            >
              <span class="mr-2">{{ $i18nPresenter.t('Name') }}</span>
              <sorting-indicator v-if="sortColumn === 'name'" :order="sortOrder" />
            </span>
          </table-heading>
          <table-heading name="name" />
          <table-heading name="accessCode">
            {{ $i18nPresenter.t('Access Code') }}
          </table-heading>

          <table-heading name="presentButton" class="lg:w-16" />
        </tr>
      </thead>

      <tbody>
        <!-- desktop folder view -->
        <template v-if="!selectedFolderId">
          <tr
            :key="`folder-${folder.folderId}`"
            v-for="folder in folders"
            @contextmenu.prevent="openKebabMenuFolder(folder, $event)"
          >
            <table-data name="thumbnail" class="main-table--thumbnail">
              <span
                @click="toFolderDetails(folder)"
                class="flex justify-center cursor-pointer"
              >
                <span class="w-20 text-center h-11">
                  <aha-icon-container
                    size="lg"
                    :margin-right="false"
                    icon-class="bg-purple-10 text-purple-50"
                  >
                    <aha-icon name="folder" />
                  </aha-icon-container>
                </span>
              </span>
            </table-data>
            <table-data
              name="name"
              class="main-table--item-name text-link"
              @click="toFolderDetails(folder)"
            >
              <a
                :title="folder.folderName"
                class="text-link"
              >
                {{ folder.folderName }}
              </a>
              <span class="flex flex-wrap items-center mt-1 font-normal leading-6">

                <aha-icon-container>
                  <aha-icon
                    name="file"
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                  />
                </aha-icon-container>
                <span class="text-xs text-base-80">
                  <span
                    v-if="folder.presentations.length <= 1"
                    v-html="$i18nPresenter.t('xPresentation', {
                      length: folder.presentations.length
                    })"
                  >
                  </span>
                  <span
                    v-else
                    v-html="$i18nPresenter.t('yPresentations', {
                      length: folder.presentations.length
                    })"
                  >
                  </span>
                </span>
              </span>
            </table-data>
            <table-data name="accessCode" class="lg:w-32" />

            <table-data name="presentButton" :showOverXlColumns="[]" class="text-right lg:w-16" />
          </tr>
        </template>

        <!-- desktop presentations list view -->
        <tr
          :key="`presentation-${presentation.id}`"
          v-for="(presentation) in presentations"
          class="table-row"
          @contextmenu.prevent="openKebabMenuPresentation(presentation, $event)"
          :class="{ 'active': presentation.selected , 'dark-folder': isDarkTheme }"
        >
          <table-data
            name="thumbnail"
            class="main-table--thumbnail main-table--table-cell"
            @click="goToPresentationDetail(presentation)"
          >
            <presentation-thumbnail :presentation="presentation" />
          </table-data>
          <table-data
            name="name"
            class="main-table--item-name text-link main-table--table-cell min-w-36"
            @click.stop="goToPresentationDetail(presentation)"
          >
            <PresentationName
              :presentation="presentation"
              :folder-name="getFolderName(presentation)"
              :is-shared-presentation="isSharedPresentation(presentation)"
            />
          </table-data>
          <table-data
            name="accessCode"
            class="text-xs lg:w-32 main-table--table-cell"
          >
            <access-code-editor
              :presentation="presentation"
              @updateAccessCode="updateAccessCode"
            />
          </table-data>

          <table-data
            name="presentButton"
            :showOverXlColumns="[]"
            class="text-right lg:w-16 main-table--table-cell"
          >
            <present-button
              v-if="!isZoomReady"
              :presentation="presentation"
              @present="onPresent"
              class="opacity-0"
            />
          </table-data>
        </tr>
      </tbody>
    </table>

    <div class="text-center" v-if="morePresentationsLoading">
      <aha-spinner />
    </div>

    <modal-view-only-warning
      @hide="handleHideViewOnlyWarningModal"
      @view="handleClickView"
      v-if="isShowingViewOnlyWarningModal"
      :active-editor="activeEditor"
    />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import EventBus from '@/eventBus'

import Utils from '@/util'
import msTeamsUtils from '@/utils/ms-teams'
// modals
import ModalViewOnlyWarning from '@/components-v2/modals/ViewOnlyWarning.vue'

// components
import PresentationName from '@/components-v2/my-presentations/main-table/PresentationName.vue'
import PresentButton from '@/components-v2/ms-teams/MSTeamsPresentButton.vue'
import PresentationThumbnail from '@/components-v2/my-presentations/main-table/PresentationThumbnail.vue'
import PresentationMode from '@/components-v2/my-presentations/main-table/PresentationMode.vue'
import SortBy from '@/components-v2/my-presentations/SortBy.vue'
import SortingIndicator from '@/components-v2/my-presentations/main-table/SortingIndicator.vue'
import TableData from '@/components-v2/my-presentations/main-table/TableData.vue'
import TableHeading from '@/components-v2/my-presentations/main-table/TableHeading.vue'
import AccessCodeEditor from '@/components-v2/my-presentations/main-table/AccessCodeEditor.vue'

export default {
  components: {
    PresentationName,
    PresentationMode,
    PresentationThumbnail,
    PresentButton,
    SortBy,
    SortingIndicator,
    TableData,
    TableHeading,
    AccessCodeEditor,
    ModalViewOnlyWarning,
  },
  data() {
    return {
      areLoadingDefaultPresentations: false,
      presentationsAndFoldersLoading: false,
      isShowingViewOnlyWarningModal: false,
      isClaimingFreeOneTimeOffer: false,
      isDecliningFreeOneTimeOffer: false,
      isShowingWrapup: true,
      activePresentation: null,
      activeEditor: null,
      isDarkTheme: false,
    }
  },
  computed: {
    ...mapGetters('zoomApp', ['isZoomReady']),
    allPresentationSelected() {
      const visiblePresentations = this.presentations.filter((presentation) => {
        const { folderId, userId } = presentation

        return folderId === this.selectedFolderId && userId === this.currentUser.id
      })

      if (visiblePresentations.length === 0) return false

      return visiblePresentations.every((presentation) => presentation.selected)
    },
    ...mapState({
      folders: (state) => state.folders,
      morePresentationsLoading: (state) => state.loaders.morePresentationsLoading,
      selectedFolderId: (state) => state.selections.selectedFolderId,
      presentations: (state) => state.presentations,
      isServerConnectable: (state) => state.indicators.isServerConnectable,
      selectedSortCriteria: (state) => state.selections.selectedSortCriteria,
      sortColumn: (state) => state.selections.selectedSortCriteria.sortColumn,
      sortOrder: (state) => state.selections.selectedSortCriteria.sortOrder,
      currentUser: (state) => state.currentUser,
    }),
    ...mapGetters(['selectedFolder']),
  },
  async created() {
    const isDarkTheme = await msTeamsUtils.isDarkThemeInMsTeams()
    this.isDarkTheme = isDarkTheme
  },
  mounted() {
    this.handleDisplayViewOnlyWarning()
  },
  methods: {
    ...mapActions([
      'setSlideActiveAction',
      'setPresentationsAction',
      'setSelectedSortCriteriaAction',
      'setCurrentUserAction',
      'setSelectedFolderIdAction',
      'getPresentationsAction',
      'getSubFoldersAction',
    ]),

    handleHideViewOnlyWarningModal() {
      this.isShowingViewOnlyWarningModal = null
    },
    handleClickView() {
      this.goToPresentationDetail(this.activePresentation)
    },
    handleDisplayViewOnlyWarning() {
      EventBus.$on('modal-view-only:before-perform-check', (presentation) => {
        this.activePresentation = presentation
      })
      EventBus.$on('modal-view-only:after-perform-check', ({ hasPerm, activeEditor }) => {
        if (!hasPerm) {
          this.isShowingViewOnlyWarningModal = true
          const { firstName, lastName } = activeEditor
          activeEditor.name = lastName ? `${firstName} ${lastName}` : firstName
          this.activeEditor = activeEditor
        }
      })
    },
    closeOtherMenu() {
      document.body.click()
    },
    openKebabMenuPresentation(presentation, event) {
      this.closeOtherMenu()
      const { x, y } = event
      EventBus.$emit('open-kebab-presentation', { x, y, id: presentation.id })
    },
    openKebabMenuFolder(folder, event) {
      this.closeOtherMenu()
      const { x, y } = event
      EventBus.$emit('open-kebab-folder', { x, y, id: folder.folderId })
    },
    getFolderName(presentation) {
      const { q: searchValue } = this.$route.query

      if (!searchValue) return null

      if (!presentation.folder) return null

      return `${presentation.folder.folderName} / `
    },
    handlePresentationSelect(changedPresentation) {
      const newPresentations = this.presentations.map((presentation) => {
        if (presentation.id === changedPresentation.id) {
          return { ...presentation, selected: !presentation.selected }
        }

        return presentation
      })

      this.setPresentationsAction(newPresentations)
    },
    async goToPresentationDetail(item) {
      this.$emit('selectPresentation', item)
    },
    handleAllPresentationSelect() {
      const newPresentations = this.presentations
        .map((presentation) => {
          const { folderId, userId } = presentation

          if (folderId === this.selectedFolderId && userId === this.currentUser.id) {
            return { ...presentation, selected: !this.allPresentationSelected }
          }

          return presentation
        })

      this.setPresentationsAction(newPresentations)
    },
    async toFolderDetails(folder) {
      this.setSelectedFolderIdAction(parseInt(folder.folderId, 10))
      this.getSubFoldersAction({
        parentFolderId: folder.folderId,
      })
      this.getPresentationsAction({
        parentFolderId: folder.folderId,
        sortColumn: this.sortColumn,
        sortOrder: this.sortOrder,
      })
      Utils.mixpanelTracking('Presentation List', 'Open folder', folder.folderId, null, {
        isMSTeamsApp: true,
      })
    },
    handleSortPresentations(sortColumn) {
      let sortOrder = 'desc'

      if (sortColumn === this.sortColumn && this.sortOrder === 'desc') {
        sortOrder = 'asc'
      }

      this.setSelectedSortCriteriaAction({
        sortColumn,
        sortOrder,
      })
    },
    async updateAccessCode(updatedItem) {
      const newPresentations = this.presentations.map((item) => {
        if (item.id === updatedItem.id) {
          return { ...item, accessCode: updatedItem.newAccessCode.toUpperCase() }
        }

        return item
      })
      this.setPresentationsAction(newPresentations)
    },
    isSharedPresentation(presentation) {
      return presentation.userId !== this.currentUser.id
    },

    async onPresent(presentation) {
      await msTeamsUtils.setPresentContext(presentation)
    },
  },
}
</script>

<style lang="scss">
.main-table {
  @apply border-separate w-full;
  border-spacing: 0 1rem;
}
.dark-folder {
  background:  #313131;
  border: 1px solid #3F4557;
  .text-dark-theme {
    color : #AFAFAF;
  }
  .text-light-in-dark-theme{
    color : white;
  }
}

.main-table tbody tr {
  @apply rounded-lg text-base-80 shadow-card;

  &:hover {
    box-shadow: 0px 25px 20px -20px rgba(0, 0, 0, .2);

    .report-button {
      @media screen and (min-width:1100px) {
        @apply flex;
      }
    }
    .present-button {
      opacity: 100;
    }
  }

  &.active {
    @apply ring-1;

    &:hover {
      @apply ring-1;
    }
  }

  transition: .5s;
  transition-timing-function: cubic-bezier(0.33, 1, 0.68, 1);
}

.main-table td.main-table--table-cell:first-child {
  @apply rounded-l-lg border-l;
}

.main-table td.main-table--table-cell:last-child {
  @apply rounded-r-lg border-r;
}

.main-table--thumbnail {
  @apply w-20 relative;
}

.main-table--item-name {
  @apply ml-4 font-bold truncate transition-colors max-w-48;
  &:hover > a {
    @apply text-primary-100;
  }
}

.published-icon {
  position: relative;
  top: 0rem;
  left: 0.25rem;
}

.published-badge {
  color: white;
  background: linear-gradient(to bottom right,#33C173 50%,white 50%);
  border-radius: 0.5rem 0rem;
  position: absolute;
  width: 2rem;
  height: 2rem;
  top: -1px;
  left: -1px;
}
</style>
